<template>
  <div :id="reference">
    <div :ref="reference" :style="containerStyle"></div>
  </div>
</template>

<script>
import lodashDebounce from "lodash/debounce";
import { SVG, easing } from "@svgdotjs/svg.js";

import { mapGetters } from "vuex";
import { MODULE_PREFIX_MONITOR } from "@/store";

import svgGaugeArc from "@/assets/svgs/svg_pulse_gauge_arc";
import svgGaugeHorizontal from "@/assets/svgs/svg_pulse_gauge_horizontal";
import { GRIDS_STABILITY_COLORS } from "@/common/constants/grids";

const EMITTED_ACTIONS = {
  FINISHED: "finished",
};

export default {
  name: "StabilityGaugeSvg",
  props: {
    reference: {
      default: "drawing",
      type: String,
    },
    pulseScoreCurrent: {
      type: [String, Number],
    },
    pulseScoreDelta: {
      type: Number,
    },
    pulseAverage: {
      type: String,
    },
    gaugeType: {
      type: String,
      validator: (value) => {
        return ["arc", "horizontal"].indexOf(value) !== -1;
      },
      default: "arc",
    },
    dark: {
      type: Boolean,
      default: true,
    },
    gaugeHolderSvgWidth: {
      type: Number,
    },
  },
  data() {
    return {
      svgCanvas: null,
      svgNotch: null,
      svgNotchPath: null,
      pulseScore: 0,
      length: null,
    };
  },
  mounted() {
    this.generate();
  },
  computed: {
    ...mapGetters({
      watchEnabled: MODULE_PREFIX_MONITOR + "enabled",
    }),
    containerStyle() {
      const style = {
        width: `${this.svgAttr.imageWidth}px`,
      };
      return style;
    },
    gaugeArc() {
      return this.gaugeType === "arc";
    },
    gaugeHorizontal() {
      return this.gaugeType === "horizontal";
    },
    svgAttr() {
      if (this.gaugeArc) {
        return {
          viewBoxWidth: 500,
          viewBoxHeight: 277,
          imageWidth: 361,
          imageHeight: 200,
        };
      }
      return {
        viewBoxWidth: 428,
        viewBoxHeight: 55,
        imageWidth:
          this.gaugeHolderSvgWidth && this.gaugeHolderSvgWidth < 450
            ? this.gaugeHolderSvgWidth - 60
            : 360,
        imageHeight: 55,
      };
    },
  },
  methods: {
    generate: function () {
      const elem = this.$refs[this.reference];
      this.svgCanvas = SVG().addTo(elem);
      const svgShape = this.gaugeArc ? svgGaugeArc : svgGaugeHorizontal;
      this.svgCanvas.svg(svgShape.svg);

      if (this.svgCanvas) {
        this.svgCanvas.viewbox(
          0,
          0,
          this.svgAttr.viewBoxWidth,
          this.svgAttr.viewBoxHeight
        );
        this.svgCanvas.size(
          `${this.svgAttr.imageWidth}px`,
          `${this.svgAttr.imageHeight}px`
        );

        this.svgNotchPath = SVG(`#${this.reference} #NotchPath`);
        if (this.svgNotchPath) {
          this.length = this.svgNotchPath.length();
        }

        this.svgNotch = SVG(`#${this.reference} #Notch`);
        this.resetNotch();

        if (!this.dark) {
          this.svgPulseScore = SVG(`#${this.reference} #PulseScore`);
          this.svgPulseScore.fill("#000000");
          this.svgPulseDirection = SVG(`#${this.reference} #PulseDirection`);
          this.svgPulseDirection.fill("#000000");
          this.svgPulseChange = SVG(`#${this.reference} #PulseChange`);
          this.svgPulseChange.fill("#000000");
          this.svgPulseAverage = SVG(`#${this.reference} #PulseAverage`);
          this.svgPulseAverage.fill("#000000");
          this.svgPulseAverageRange = SVG(
            `#${this.reference} #PulseAverageRange`
          );
          this.svgPulseAverageRange.fill("#000000");
          this.svgPulseUnstable = SVG(`#${this.reference} #PulseUnstable`);
          this.svgPulseUnstable.fill("#000000");
          this.svgPulseUnstable.font("weight", "normal");
          this.svgPulseStable = SVG(`#${this.reference} #PulseStable`);
          this.svgPulseStable.fill("#000000");
          this.svgPulseStable.font("weight", "normal");
        }

        this.svgPulseVeryLow = SVG(`#${this.reference} #PulseVeryLow`);
        this.svgPulseVeryLow.fill(GRIDS_STABILITY_COLORS.verylow);
        this.svgPulseLow = SVG(`#${this.reference} #PulseLow`);
        this.svgPulseLow.fill(GRIDS_STABILITY_COLORS.low);
        this.svgPulseNeutral = SVG(`#${this.reference} #PulseNeutral`);
        this.svgPulseNeutral.fill(GRIDS_STABILITY_COLORS.neutral);
        this.svgPulseHigh = SVG(`#${this.reference} #PulseHigh`);
        this.svgPulseHigh.fill(GRIDS_STABILITY_COLORS.high);
        this.svgPulseVeryHigh = SVG(`#${this.reference} #PulseVeryHigh`);
        this.svgPulseVeryHigh.fill(GRIDS_STABILITY_COLORS.veryhigh);

        this.updatePulseScore();
        this.updatePulseChange();
        this.updatePulseAverage();
        this.updateWatchEnabled();

        this.animate();
      }
    },
    resetNotch() {
      if (this.svgNotch) {
        this.pulseScore = 0;
        const point = this.svgNotchPath.pointAt(0);
        this.svgNotch.center(point.x, point.y);
      }
    },
    updatePulseScore() {
      let svgTextPulseScore = SVG(`#${this.reference} #PulseScore`);
      if (svgTextPulseScore && svgTextPulseScore.node) {
        if (this.gaugeArc && this.pulseScoreDelta === 0) {
          svgTextPulseScore.x(185);
        }
        svgTextPulseScore.node.textContent = this.pulseScore;
      }
    },
    updatePulseChange() {
      let svgTextPulseDirection = SVG(`#${this.reference} #PulseDirection`);
      if (svgTextPulseDirection && svgTextPulseDirection.node) {
        let direction = "";
        if (this.pulseScoreDelta > 0) {
          direction = "↑";
        } else if (this.pulseScoreDelta < 0) {
          direction = "↓";
        }
        svgTextPulseDirection.node.textContent = direction;
      }
      let svgTextPulseChange = SVG(`#${this.reference} #PulseChange`);
      if (svgTextPulseChange && svgTextPulseChange.node) {
        let change = "";
        if (this.pulseScoreDelta !== 0) {
          change = Math.abs(this.pulseScoreDelta);
        }
        svgTextPulseChange.node.textContent = change;
      }
    },
    updatePulseAverage() {
      let svgTextPulseAverage = SVG(`#${this.reference} #PulseAverageRange`);
      if (svgTextPulseAverage && svgTextPulseAverage.node) {
        svgTextPulseAverage.node.textContent = this.pulseAverage;
      }
    },
    updatePulseSvg() {
      this.updatePulseScore();
      this.updatePulseChange();
      this.updatePulseAverage();
    },
    updateWatchEnabled() {
      if (this.watchEnabled && this.gaugeArc) {
        const svgTextPulseInfo = SVG(`#${this.reference} #PulseInfo`);
        svgTextPulseInfo.y(75);
      }
    },
    animate() {
      lodashDebounce(() => {
        const runner = this.svgNotch.animate({ duration: 3000 });
        runner.during((pos) => {
          const eased = easing["<>"](pos);
          if (eased >= parseInt(this.pulseScoreCurrent) / 100) {
            return;
          }
          const point = this.svgNotchPath.pointAt(eased * this.length);
          this.pulseScore = Math.round(eased * 100);
          this.svgNotch.center(point.x, point.y);
        });
        runner.after(() => {
          // setting pulseScore and notch center just in case the animation locks up
          // we can make sure the pulse score displayed and the notch are in correct spot
          this.pulseScore = this.pulseScoreCurrent;
          if (this.gaugeHorizontal) {
            const notchX = (parseInt(this.pulseScoreCurrent) / 100) * 338 + 90;
            this.svgNotch.center(notchX, this.svgNotch.cy());
          }
          this.$emit(EMITTED_ACTIONS.FINISHED, true);
        });
      }, 1000)();
    },
  },
  watch: {
    pulseScore() {
      this.updatePulseScore();
    },
    pulseScoreCurrent() {
      this.resetNotch();
      this.updatePulseScore();
      this.animate();
    },
    pulseScoreDelta() {
      this.updatePulseSvg();
    },
    pulseAverage() {
      this.updatePulseSvg();
    },
    watchEnabled() {
      this.updateWatchEnabled();
    },
  },
};
</script>

<style scoped></style>
